
import { Check, X, Zap, DollarSign, Store } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

export const LandingProblem = () => {
    return (
        <section className="py-20 bg-gray-50/50">
            <div className="container mx-auto px-4">
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                        Most giveaway platforms are built for enterprises — not small businesses.
                    </h2>
                </div>

                <div className="grid md:grid-cols-3 gap-8">
                    <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 text-center">
                        <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <X className="w-6 h-6 text-red-600" />
                        </div>
                        <h3 className="font-bold text-gray-900 mb-2">Expensive Plans</h3>
                        <p className="text-gray-500">Expensive plans just to unlock basic features you actually need.</p>
                    </div>

                    <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 text-center">
                        <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <X className="w-6 h-6 text-red-600" />
                        </div>
                        <h3 className="font-bold text-gray-900 mb-2">Confusing Limits</h3>
                        <p className="text-gray-500">Confusing entry limits and surprise upgrade prompts when you succeed.</p>
                    </div>

                    <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 text-center">
                        <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <X className="w-6 h-6 text-red-600" />
                        </div>
                        <h3 className="font-bold text-gray-900 mb-2">Overbuilt Dashboards</h3>
                        <p className="text-gray-500">Complex, overbuilt dashboards that take hours to learn and configure.</p>
                    </div>
                </div>
            </div>
        </section>
    );
};

export const LandingSolution = () => {
    return (
        <section className="py-20 bg-white">
            <div className="container mx-auto px-4">
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                        We built RaffleKit differently.
                    </h2>
                </div>

                <div className="grid md:grid-cols-3 gap-8">
                    <div className="group hover:-translate-y-1 transition-transform duration-300">
                        <div className="h-full bg-white p-8 rounded-2xl shadow-lg border border-gray-100 relative overflow-hidden">
                            <div className="absolute top-0 right-0 p-8 opacity-5">
                                <Zap className="w-24 h-24 text-primary" />
                            </div>
                            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-6 text-primary">
                                <Zap className="w-6 h-6" />
                            </div>
                            <h3 className="font-bold text-xl text-gray-900 mb-3">Dead Simple</h3>
                            <p className="text-gray-500">Launch a giveaway in under 5 minutes. No coding, no complex settings.</p>
                        </div>
                    </div>

                    <div className="group hover:-translate-y-1 transition-transform duration-300">
                        <div className="h-full bg-white p-8 rounded-2xl shadow-lg border border-gray-100 relative overflow-hidden">
                            <div className="absolute top-0 right-0 p-8 opacity-5">
                                <DollarSign className="w-24 h-24 text-primary" />
                            </div>
                            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-6 text-primary">
                                <DollarSign className="w-6 h-6" />
                            </div>
                            <h3 className="font-bold text-xl text-gray-900 mb-3">Transparent Pricing</h3>
                            <p className="text-gray-500">Clear limits. No feature traps. Know exactly what you pay for.</p>
                        </div>
                    </div>

                    <div className="group hover:-translate-y-1 transition-transform duration-300">
                        <div className="h-full bg-white p-8 rounded-2xl shadow-lg border border-gray-100 relative overflow-hidden">
                            <div className="absolute top-0 right-0 p-8 opacity-5">
                                <Store className="w-24 h-24 text-primary" />
                            </div>
                            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-6 text-primary">
                                <Store className="w-6 h-6" />
                            </div>
                            <h3 className="font-bold text-xl text-gray-900 mb-3">Built for Small Business</h3>
                            <p className="text-gray-500">The features you need — nothing bloated. Perfect for growth.</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export const LandingHowItWorks = () => {
    return (
        <section className="py-20 bg-gray-50">
            <div className="container mx-auto px-4">
                <div className="grid md:grid-cols-3 gap-8 text-center">
                    <div className="relative">
                        <div className="text-9xl font-bold text-gray-200/50 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 -z-10">1</div>
                        <h3 className="font-bold text-xl text-gray-900 mb-2 mt-8">Create your contest</h3>
                        <p className="text-gray-500 max-w-xs mx-auto">Set up your prizes and entry methods in minutes.</p>
                    </div>

                    <div className="relative">
                        <div className="text-9xl font-bold text-gray-200/50 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 -z-10">2</div>
                        <h3 className="font-bold text-xl text-gray-900 mb-2 mt-8">Share your link</h3>
                        <p className="text-gray-500 max-w-xs mx-auto">Promote your unique link or embed the widget on your site.</p>
                    </div>

                    <div className="relative">
                        <div className="text-9xl font-bold text-gray-200/50 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 -z-10">3</div>
                        <h3 className="font-bold text-xl text-gray-900 mb-2 mt-8">Pick a winner</h3>
                        <p className="text-gray-500 max-w-xs mx-auto">Automatically select winners and verify their entries instantly.</p>
                    </div>
                </div>

                <div className="text-center mt-12 flex flex-col sm:flex-row justify-center gap-4">
                    <Button asChild size="lg" className="rounded-full shadow-lg hover:shadow-glow font-bold">
                        <Link to="/admin?mode=signup">Start Your First Contest</Link>
                    </Button>
                    <Button asChild variant="outline" size="lg" className="rounded-full shadow-lg font-bold bg-white">
                        <Link to="/how-it-works">See How It Works</Link>
                    </Button>
                </div>
            </div>
        </section>
    );
};
